import {
	useApiGetIntegrationTimeseries,
	type ApiGetIntegrationTimeseriesVariables,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { integrationList } from '@repo/common/constants/integration/integrations';
import { keepPreviousData } from '@tanstack/react-query';
import { memo, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuthUser, useIntegrationList } from '../../api';
import { AnalyticsBanner } from './components/AnalyticsBanner';
import type { IntegrationMetrics } from './components/cards/MetricsIntegrationCards';
import { MetricsIntegrationCards } from './components/cards/MetricsIntegrationCards';
import { IntegrationsOverviewChart } from './components/charts/IntegrationsOverviewChart';
import { MetricsOverviewTab } from './components/MetricsOverviewTab';
import { MetricsOverviewTabSkeleton } from './components/MetricsOverviewTabSkeleton';
import { useOverviewMetricDatetime } from './hooks/useMetricOverviewDatetime';
import type {
	IntegrationFilterType,
	IntegrationsOverviewTabControllerProps,
} from './types';

const SETTINGS_PAGE_ONLY_TYPES = integrationList
	.filter((i) => i.isExtension)
	.map((i) => i.type)
	.join(',');

const DEFAULT_REQUIRED_SEARCH_PARAMS = {
	type__not_in: SETTINGS_PAGE_ONLY_TYPES,
};

import { isValidTimeRange, isValidViewOption } from './types.ts';
export const IntegrationsOverviewTabController = memo(
	({
		onMetricCardClick,
		integrations,
	}: IntegrationsOverviewTabControllerProps) => {
		const { user, workspace } = useAuthUser();
		const {
			timeRangeValue,
			setTimeRangeValue,
			dateRangeOption,
			setDateRangeOption,
			dateFilter,
			setDateFilter,
		} = useOverviewMetricDatetime({ cacheKey: 'integrations' });
		const navigate = useNavigate();
		const [filterType, setFilterType] = useState<IntegrationFilterType>('all');

		// Create wrapper functions to handle type conversion with validation
		const handleSetDateFilter = useCallback(
			(value: string) => {
				if (isValidTimeRange(value)) {
					setDateFilter(value);
				}
			},
			[setDateFilter]
		);

		const handleSetDateRangeOption = useCallback(
			(value: string) => {
				if (isValidViewOption(value)) {
					setDateRangeOption(value);
				}
			},
			[setDateRangeOption]
		);

		const integrationFilterValues = useMemo(
			() => [
				{ label: 'All integrations', value: 'all' },
				...integrations.map((integration) => ({
					label: integration.name,
					value: integration.id,
				})),
			],
			[integrations]
		);

		const { data: integrationsData, isLoading: isIntegrationsDataLoading } =
			useIntegrationList({
				page: 1,
				filters: DEFAULT_REQUIRED_SEARCH_PARAMS,
			});

		const filteredIntegrations = useMemo(() => {
			if (!integrationsData?.results) return [];
			if (filterType === 'all') return integrationsData.results;
			return integrationsData.results.filter(
				(integration) => integration.id === filterType
			);
		}, [integrationsData?.results, filterType]);

		const metrics: IntegrationMetrics = {
			status_counts: {
				normal:
					filteredIntegrations.filter(
						(integration) => integration.status === 'success'
					).length || 0,
				error:
					filteredIntegrations.filter(
						(integration) => integration.status === 'failed'
					).length || 0,
			},
		};

		const { data: timeseries, isPending: isTimeseriesLoading } =
			useApiGetIntegrationTimeseries(
				{
					queryParams: {
						...(filterType === 'all' ? {} : { integration_id: filterType }),
						time_range: dateFilter,
						view_option: dateRangeOption,
					},
				} as ApiGetIntegrationTimeseriesVariables,
				{ placeholderData: keepPreviousData }
			);

		const handleFilterTypesChange = useCallback(
			(filterTypes: string[]) => {
				const newFilterType = filterTypes[0] as IntegrationFilterType;
				setFilterType(newFilterType);

				const filterLabel = integrationFilterValues.find(
					(opt) => opt.value === newFilterType
				)?.label;
				if (!filterLabel) return;

				trackEvent(
					'integrations/overview/filter_change',
					{
						filter_type: filterLabel,
						filter_value: newFilterType,
					},
					user,
					workspace!
				);
			},
			[user, workspace, integrationFilterValues]
		);

		const handleNavigate = useCallback(() => {
			navigate(`/analytics`);
		}, [navigate]);

		if (
			isIntegrationsDataLoading ||
			isTimeseriesLoading ||
			!timeseries ||
			!metrics
		) {
			return <MetricsOverviewTabSkeleton cards={2} />;
		}

		return (
			<MetricsOverviewTab
				filterOptions={[integrationFilterValues]}
				filterTypes={[filterType]}
				setFilterTypes={handleFilterTypesChange}
			>
				<MetricsIntegrationCards
					integrationMetrics={metrics}
					onMetricCardClick={onMetricCardClick}
				/>
				<AnalyticsBanner handleNavigate={handleNavigate} />
				<IntegrationsOverviewChart
					timeseries={timeseries}
					dateFilter={dateFilter || '24h'}
					setDateFilter={handleSetDateFilter}
					setTimeRangeValue={setTimeRangeValue}
					timeRangeValue={timeRangeValue}
					dateRangeOption={dateRangeOption || 'day'}
					setDateRangeOption={handleSetDateRangeOption}
				/>
			</MetricsOverviewTab>
		);
	}
);
